import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { fetchRequest } from "#/lib/stream-api";
import queryKeys from "#/lib/query-keys";
import { useAtomValue } from "jotai";
import { atoms } from "#/lib/atoms/atoms";
import { components } from "#/types.ts/swagger";

export function useDspLocationsQuery() {
  const mid = useAtomValue(atoms.mid);

  return useSuspenseQuery<components["schemas"]["GetDspLocationsResponseDto"]>({
    queryKey: queryKeys.dspLocations(mid),
    // Override the global `staleTime: Infinity` / `refetchOnWindowFocus: false`
    // (lib/query.ts) for this query only. DSP busy/pause/connection state moves
    // without us — busy windows auto-lapse server-side, and a colleague can
    // pause a platform from another device — so a cache entry that is never
    // stale is simply wrong here. It also meant that any transient disagreement
    // with the server was permanent: with data never stale, `refetchOnMount` is
    // a no-op too, so even navigating away and back would not refetch, and a
    // hard reload was the only repair (STREAM-3450's stuck busy banner).
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    // Pass `merchant_id` explicitly so the fetch is pinned to the atom-derived
    // mid baked into the query key. Without this, the request falls back to
    // `context()` in `lib/stream-api.ts`, which reads `merchant_id` from a
    // cookie — and any code path that updates the atom without syncing the
    // cookie (e.g. a missed call to `setCurrentOrgMerchant`) silently
    // returns the OLD merchant's DSP bindings. See plan:
    // fix-dsp-connections-after-in-page-merchant-switch.
    queryFn: ({ queryKey }) =>
      fetchRequest(`/dsp/locations`, {
        method: "get",
        headers: { merchant_id: queryKey[1] as string },
      }),
  });
}
